import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { toast } from 'sonner';
import { VideoFeatures } from '@/store/videoFeatures';
import { motion } from 'framer-motion';
import { SectionCard } from '@/components/ui/SectionCard';
import { CompactToggleRow } from '@/components/ui/CompactToggleRow';
import {
  FastForward,
  Rewind,
  Volume2,
  Clock,
  Maximize,
  PlayCircle,
  Play,
  Gauge,
  EyeOff,
  Minus,
  PictureInPicture2,
  Cast,
  PanelBottom,
  Clapperboard,
  Lock
} from 'lucide-react';

// Variantes de animação stagger para os grupos
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.02
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 6 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.2,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};

interface VideoControlsTabProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

export const VideoControlsTab: React.FC<VideoControlsTabProps> = ({
  features,
  setFeature,
}) => {
  useLanguage();
  // Função para ativar um controle e desativar "Desativar Controles"
  const handleControlToggle = <K extends keyof VideoFeatures>(
    key: K,
    checked: boolean
  ) => {
    setFeature(key, checked as VideoFeatures[K]);

    // Se estiver ativando algum controle, desativa "Desativar Controles"
    if (checked && features.disableControls) {
      setFeature('disableControls', false);
      setFeature('noneControlsMoreAutoplay', false);
    }

    // Se estiver ativando algum sub-controle, desativa a BARRA FAKE
    // (recurso independente, não confundir com showProgressBar — sub-recurso
    // dos controles que pode coexistir e tem switch próprio nesta mesma tab).
    if (checked && features.progressBar?.enabled) {
      setFeature('progressBar', {
        ...(features.progressBar || {}),
        enabled: false,
      });
      toast.info(translateInterfaceText("Barra fake foi desativada automaticamente. Barra fake e controles não podem estar ativos ao mesmo tempo."));
    }
  };

  // Função para ativar "Desativar Controles" e desativar todos os outros
  const handleDisableControlsToggle = (checked: boolean) => {
    setFeature('disableControls', checked);
    setFeature('noneControlsMoreAutoplay', checked);

    // Se estiver ativando "Desativar Controles", desativa todos os outros
    if (checked) {
      setFeature('skipForward', false);
      setFeature('rewind', false);
      setFeature('showCurrentTime', false);
      setFeature('showProgressBar', false);
      setFeature('bigPlayButton', false);
      setFeature('smallPlayButton', false);
      setFeature('volumeControl', false);
      setFeature('fullScreen', false);
      setFeature('playbackSpeedControl', false);
      setFeature('pictureInPicture', false);
      setFeature('controlBarEnabled', false);
    }
  };

  // Verifica se os controles devem estar desabilitados
  const isDisabled = features.disableControls || false;

  return (
    <motion.div
      className="space-y-4"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Grupos de controles */}
      <>
        {/* CARD 1 - NAVEGAÇÃO DO VÍDEO (Grid 2 colunas) */}
        <motion.div variants={itemVariants}>
          <SectionCard
            title={translateInterfaceText("Navegação do Vídeo")}
            description={translateInterfaceText("Facilite o controle de tempo durante a reprodução.")}
            showDividers
          >
            <div className="grid grid-cols-2 gap-0.5">
                <CompactToggleRow
                  icon={<FastForward className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Avançar 10s")}
                  description={translateInterfaceText("Permite pular rapidamente trechos")}
                  checked={features.skipForward}
                  onCheckedChange={(checked) => handleControlToggle('skipForward', checked)}
                  disabled={isDisabled}
                />
                <CompactToggleRow
                  icon={<Rewind className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Retroceder 10s")}
                  description={translateInterfaceText("Volte para revisar momentos")}
                  checked={features.rewind}
                  onCheckedChange={(checked) => handleControlToggle('rewind', checked)}
                  disabled={isDisabled}
                />
                <CompactToggleRow
                  icon={<Clock className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Tempo Atual")}
                  description={translateInterfaceText("Mostra o progresso do vídeo")}
                  checked={features.showCurrentTime || false}
                  onCheckedChange={(checked) => handleControlToggle('showCurrentTime', checked)}
                  disabled={isDisabled}
                />
                <CompactToggleRow
                  icon={<Maximize className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Tela Cheia")}
                  description={translateInterfaceText("Experiência imersiva")}
                  checked={features.fullScreen || false}
                  onCheckedChange={(checked) => handleControlToggle('fullScreen', checked)}
                  disabled={isDisabled}
                />
                <CompactToggleRow
                  icon={<Minus className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Barra de Progresso")}
                  description={translateInterfaceText("Mostra o progresso visual do vídeo")}
                  checked={features.showProgressBar || false}
                  onCheckedChange={(checked) => handleControlToggle('showProgressBar', checked)}
                  disabled={isDisabled}
                  className="col-span-2 sm:col-span-1"
                />
                <CompactToggleRow
                  icon={<PanelBottom className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Rodapé")}
                  description={translateInterfaceText("Mostra os controles no rodapé do player")}
                  tooltipText={translateInterfaceText("Exibe a barra inferior com os controles do player. Quando desativada, os controles ficam ocultos mesmo com o tema personalizado.")}
                  checked={features.controlBarEnabled || false}
                  onCheckedChange={(checked) => handleControlToggle('controlBarEnabled', checked)}
                  disabled={isDisabled}
                />
                <CompactToggleRow
                  icon={<Clapperboard className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Storyboard")}
                  description={translateInterfaceText("Miniaturas ao passar o mouse na barra")}
                  tooltipText={translateInterfaceText("Exibe miniaturas do vídeo ao passar o mouse na barra de progresso. Requer controles do player ativos (não funciona com a barra fake). Disponível apenas em vídeos processados após esta atualização.")}
                  checked={features.storyboardEnabled || false}
                  onCheckedChange={(checked) => handleControlToggle('storyboardEnabled', checked)}
                  disabled={isDisabled || !features.storyboardUrl}
                  disabledTooltip={!features.storyboardUrl ? translateInterfaceText("Este vídeo foi enviado antes do recurso de Storyboard estar disponível. Para ativar, faça o upload do vídeo novamente.") : undefined}
                />
              </div>
            </SectionCard>
          </motion.div>

          {/* CARD 2 - INTERAÇÃO */}
          <motion.div variants={itemVariants}>
            <SectionCard
              title={translateInterfaceText("Interação com Vídeo")}
              description={translateInterfaceText("Configure os botões de reprodução e volume.")}
              showDividers
            >
              <div className="grid grid-cols-2 gap-0.5">
                {/* Esquerda: Play Grande */}
                <CompactToggleRow
                  icon={<PlayCircle className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Play Grande")}
                  description={translateInterfaceText("Destaca o botão central de play")}
                  checked={features.bigPlayButton}
                  onCheckedChange={(checked) => handleControlToggle('bigPlayButton', checked)}
                  disabled={isDisabled}
                />
                {/* Direita: Play Pequeno */}
                <CompactToggleRow
                  icon={<Play className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Play Pequeno")}
                  description={translateInterfaceText("Botão discreto sobre o vídeo")}
                  checked={features.smallPlayButton}
                  onCheckedChange={(checked) => handleControlToggle('smallPlayButton', checked)}
                  disabled={isDisabled}
                />
                {/* Esquerda: Controle de Volume */}
                <CompactToggleRow
                  icon={<Volume2 className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Volume")}
                  description={translateInterfaceText("Permite ajustar o áudio")}
                  checked={features.volumeControl}
                  onCheckedChange={(checked) => handleControlToggle('volumeControl', checked)}
                  disabled={isDisabled}
                />
                {/* Picture-in-Picture */}
                <CompactToggleRow
                  icon={<PictureInPicture2 className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Tela flutuante")}
                  description={translateInterfaceText("Permite assistir em janela flutuante")}
                  tooltipText={translateInterfaceText("Exibe um botão para abrir o vídeo em uma janela flutuante (PiP), permitindo que o espectador continue assistindo enquanto navega em outras abas.")}
                  checked={features.pictureInPicture || false}
                  onCheckedChange={(checked) => handleControlToggle('pictureInPicture', checked)}
                  disabled={isDisabled}
                />
                {/* Smart TV (AirPlay no Safari + Chromecast no Chrome) */}
                <CompactToggleRow
                  icon={<Cast className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Smart TV")}
                  description={translateInterfaceText("Botão para transmitir o vídeo")}
                  tooltipText={translateInterfaceText("Adiciona um botão nos controles que abre o seletor nativo de dispositivos do navegador (AirPlay no Safari, Chromecast no Chrome). Só aparece pra quem tem receptor compatível na rede.")}
                  checked={features.tvCastEnabled || false}
                  onCheckedChange={(checked) => handleControlToggle('tvCastEnabled', checked)}
                  disabled={isDisabled}
                />
              </div>
            </SectionCard>
          </motion.div>

          {/* CARD 3 - COMPORTAMENTO */}
          <motion.div variants={itemVariants}>
            <SectionCard
              title={translateInterfaceText("Comportamento")}
              description={translateInterfaceText("Ajuste a visibilidade dos controles do player.")}
              showDividers
            >
              <div className="grid grid-cols-2 gap-0.5">
                <CompactToggleRow
                  icon={<Gauge className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Velocidade")}
                  description={translateInterfaceText("Permite ajustar a velocidade do vídeo")}
                  checked={features.playbackSpeedControl || false}
                  onCheckedChange={(checked) => handleControlToggle('playbackSpeedControl', checked)}
                  disabled={isDisabled}
                />
                <CompactToggleRow
                  icon={<Lock className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Bloquear pause")}
                  description={translateInterfaceText("Impede o espectador de pausar o vídeo")}
                  tooltipText={translateInterfaceText("Quando ativo, o espectador não consegue pausar o vídeo (clique, botão de pause). Funciona com ou sem controles e com autoplay.")}
                  checked={features.blockPause || false}
                  onCheckedChange={(checked) => setFeature('blockPause', checked)}
                />
                <CompactToggleRow
                  icon={<EyeOff className="w-full h-full" strokeWidth={2} />}
                  title={translateInterfaceText("Desativar")}
                  description={translateInterfaceText("Remove todos os controles do player")}
                  tooltipText={translateInterfaceText("Desativa completamente os controles do player, deixando apenas o vídeo visível.")}
                  checked={features.disableControls || false}
                  onCheckedChange={handleDisableControlsToggle}
                />
              </div>
            </SectionCard>
          </motion.div>
        </>
    </motion.div>
  );
};
